import { useState } from 'react'
import { Dialog, DialogContent, DialogTitle, Divider, Grid, IconButton, Typography, Button, CircularProgress, TextField } from "@mui/material"
import CloseIcon from '@mui/icons-material/Close'
import SendIcon from '@mui/icons-material/Send'
import DownloadIcon from '@mui/icons-material/Download'
import { formatDateHourMinute } from "../../utils/strings"

export interface ParticipanteI {
    id: number
    nombreProfesor: string
    email: string
    edad: string
    ciudad: string
    puesto: string
    nivelEducativo: string
    comoSeEntero: string
    interes: string
    codigoRegistro: string
    fechaAsistencia: string
    fechaAlta: string
}

interface Props {
    participante: ParticipanteI | null
    onClose: () => void
}

function descargarPase(qr: string, codigo: string, nombre: string) {
    const scale = 2
    const W = 460, H = 240
    const canvas = document.createElement('canvas')
    canvas.width = W * scale
    canvas.height = H * scale
    const ctx = canvas.getContext('2d')!
    ctx.scale(scale, scale)

    const roundRect = (x: number, y: number, w: number, h: number, r: number) => {
        ctx.beginPath()
        ctx.moveTo(x + r, y); ctx.lineTo(x + w - r, y)
        ctx.quadraticCurveTo(x + w, y, x + w, y + r)
        ctx.lineTo(x + w, y + h - r)
        ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h)
        ctx.lineTo(x + r, y + h)
        ctx.quadraticCurveTo(x, y + h, x, y + h - r)
        ctx.lineTo(x, y + r)
        ctx.quadraticCurveTo(x, y, x + r, y)
        ctx.closePath()
    }

    ctx.fillStyle = '#ffffff'
    roundRect(0, 0, W, H, 16)
    ctx.fill()

    ctx.fillStyle = '#0076d0'
    ctx.beginPath()
    ctx.moveTo(16, 0); ctx.lineTo(W - 16, 0)
    ctx.quadraticCurveTo(W, 0, W, 16)
    ctx.lineTo(W, 48); ctx.lineTo(0, 48); ctx.lineTo(0, 16)
    ctx.quadraticCurveTo(0, 0, 16, 0)
    ctx.closePath()
    ctx.fill()

    ctx.fillStyle = '#ffffff'
    ctx.font = 'bold 13px sans-serif'
    ctx.textAlign = 'center'
    ctx.fillText('TU PASE AL EVENTO', W / 2, 30)

    ctx.textAlign = 'left'
    ctx.fillStyle = '#0076d0'
    ctx.font = 'bold 9px sans-serif'
    ctx.fillText('NOMBRE', 24, 74)
    ctx.fillStyle = '#111827'
    ctx.font = 'bold 20px sans-serif'
    ctx.fillText(nombre, 24, 98)

    ctx.setLineDash([4, 4])
    ctx.strokeStyle = '#d1d5db'
    ctx.lineWidth = 1
    ctx.beginPath(); ctx.moveTo(24, 115); ctx.lineTo(W - 150, 115); ctx.stroke()
    ctx.setLineDash([])

    ctx.fillStyle = '#0076d0'
    ctx.font = 'bold 9px sans-serif'
    ctx.fillText('CÓDIGO DE REGISTRO', 24, 135)
    ctx.fillStyle = '#e91e8c'
    ctx.font = 'bold 16px sans-serif'
    ctx.fillText(codigo, 24, 158)

    const qrImg = new Image()
    qrImg.crossOrigin = 'anonymous'
    qrImg.onload = () => {
        const qrX = W - 140, qrY = 60, qrSize = 110
        ctx.strokeStyle = '#e5e7eb'
        ctx.lineWidth = 1
        ctx.strokeRect(qrX - 4, qrY - 4, qrSize + 8, qrSize + 8)
        ctx.drawImage(qrImg, qrX, qrY, qrSize, qrSize)
        const link = document.createElement('a')
        link.download = `pase-evento-${codigo}.png`
        link.href = canvas.toDataURL('image/png')
        link.click()
    }
    qrImg.src = qr
}

const Field = ({ label, value }: { label: string; value: string }) => (
    <Grid item xs={12} sm={6}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="body1">{value || '—'}</Typography>
    </Grid>
)

const ParticipanteModal = ({ participante, onClose }: Props) => {
    const [sending, setSending] = useState(false)
    const [status, setStatus] = useState<'idle' | 'ok' | 'error'>('idle')
    const [targetEmail, setTargetEmail] = useState('')

    if (!participante) return null

    if (!targetEmail && participante.email && targetEmail !== participante.email) {
        setTargetEmail(participante.email)
    }

    const qrUrl = `${process.env.REACT_APP_API_URL}/images/QR/${participante.codigoRegistro}.png`

    const handleReenviar = async () => {
        setSending(true)
        setStatus('idle')
        try {
            const res = await fetch(qrUrl)
            const blob = await res.blob()
            const base64 = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader()
                reader.onloadend = () => resolve(reader.result as string)
                reader.onerror = reject
                reader.readAsDataURL(blob)
            })

            await fetch('https://barrilito.com.mx/api/evento/sendQr', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    nombre: participante.nombreProfesor,
                    email: targetEmail,
                    codigo: participante.codigoRegistro,
                    qrBase64: base64
                })
            })

            setStatus('ok')
        } catch {
            setStatus('error')
        } finally {
            setSending(false)
        }
    }

    return (
        <Dialog open={!!participante} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                {participante.nombreProfesor}
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <Divider />
            <DialogContent>
                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Field label="Código de registro" value={participante.codigoRegistro} />
                    {participante.codigoRegistro && (
                        <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, my: 1 }}>
                            <img
                                src={qrUrl}
                                alt={`QR ${participante.codigoRegistro}`}
                                style={{ width: 180, height: 180, objectFit: 'contain' }}
                            />
                            <TextField
                                label="Enviar a"
                                size="small"
                                value={targetEmail}
                                onChange={e => { setTargetEmail(e.target.value); setStatus('idle') }}
                                sx={{ width: 280 }}
                            />
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={sending ? <CircularProgress size={14} /> : <SendIcon />}
                                onClick={handleReenviar}
                                disabled={sending || !targetEmail}
                            >
                                {sending ? 'Enviando...' : 'Reenviar QR'}
                            </Button>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<DownloadIcon />}
                                onClick={() => descargarPase(qrUrl, participante.codigoRegistro, participante.nombreProfesor)}
                            >
                                Descargar pase
                            </Button>
                            {status === 'ok' && (
                                <Typography variant="caption" color="success.main">QR enviado correctamente</Typography>
                            )}
                            {status === 'error' && (
                                <Typography variant="caption" color="error">Error al enviar el QR</Typography>
                            )}
                        </Grid>
                    )}
                    <Field label="Email" value={participante.email} />
                    <Field label="Edad" value={participante.edad} />
                    <Field label="Ciudad" value={participante.ciudad} />
                    <Field label="Puesto" value={participante.puesto} />
                    <Field label="Nivel educativo" value={participante.nivelEducativo} />
                    <Field label="¿Cómo se enteró?" value={participante.comoSeEntero} />
                    <Field label="Interés" value={participante.interes} />
                    <Field label="Fecha de asistencia" value={participante.fechaAsistencia ? formatDateHourMinute(participante.fechaAsistencia) : '—'} />
                    <Field label="Fecha de registro" value={participante.fechaAlta ? formatDateHourMinute(participante.fechaAlta) : '—'} />
                </Grid>
            </DialogContent>
        </Dialog>
    )
}

export default ParticipanteModal
